import type { ConnectorConnection } from '@saaga/shared/interfaces/connector.interface'
import { useRevokeConnectionMutation } from '@saaga/shared/store/connector-endpoints.store'
import { useUpdateProjectActiveMutation } from '@saaga/shared/store/connector-project-endpoints.store'
import { logger } from '@saaga/shared/utils/logger.util'
import { useCallback, useState } from 'react'
import { toast } from 'sonner'

import { useConnectorOAuth } from './use-connector-oauth.hook'

export interface UseConnectorHandlersReturn {
  connectingProviderSlug: string | null
  connectionToDisconnect: ConnectorConnection | null
  handleConnect: (providerSlug: string) => void
  handleDisconnect: (providerSlug: string) => void
  handleDisconnectCancel: () => void
  handleDisconnectConfirm: () => Promise<void>
  handleToggleProjectActive: (projectId: string, isActive: boolean) => void
}

export const useConnectorHandlers = (
  connectionList: ConnectorConnection[]
): UseConnectorHandlersReturn => {
  const [connectionToDisconnect, setConnectionToDisconnect] = useState<ConnectorConnection | null>(
    null
  )

  const { handleConnect, connectingProviderSlug } = useConnectorOAuth()
  const [revokeConnection] = useRevokeConnectionMutation()
  const [updateProjectActive] = useUpdateProjectActiveMutation()

  const handleDisconnect = useCallback(
    (providerSlug: string) => {
      const connection = connectionList.find((c) => c.provider === providerSlug)
      if (connection) setConnectionToDisconnect(connection)
    },
    [connectionList]
  )

  const handleDisconnectCancel = useCallback(() => setConnectionToDisconnect(null), [])

  const handleDisconnectConfirm = useCallback(async () => {
    if (!connectionToDisconnect) return
    try {
      await revokeConnection(connectionToDisconnect.id).unwrap()
      toast.success('Connector disconnected')
    } catch (error) {
      logger.error('Failed to revoke connection', error)
      toast.error('Failed to disconnect')
    }
    setConnectionToDisconnect(null)
  }, [connectionToDisconnect, revokeConnection])

  const handleToggleProjectActive = useCallback(
    async (projectId: string, isActive: boolean) => {
      try {
        await updateProjectActive({ projectId, isActive }).unwrap()
        toast.success(isActive ? 'Project activated' : 'Project deactivated')
      } catch (error) {
        logger.error('Failed to update project', error)
        toast.error('Failed to update project')
      }
    },
    [updateProjectActive]
  )

  return {
    connectingProviderSlug,
    connectionToDisconnect,
    handleConnect,
    handleDisconnect,
    handleDisconnectCancel,
    handleDisconnectConfirm,
    handleToggleProjectActive,
  }
}
